import { X, Calendar as CalendarIcon, Search, Square, CheckSquare, MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Table } from "@tanstack/react-table";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Calendar } from "components/ui/calendar";
import { cn } from "lib/utils";
import { Combobox } from "components/ui/combobox";
import { AlertCardSkeleton } from "./alert-card-skeleton";
import { AlertCardComponent, AlertCard } from "./alert-card";
import { useLocation } from "react-router-dom";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export interface ComboboxOption {
  value: string
  label: string
}
export interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  buttonClassName?: string
}

interface DateRange {
  from?: Date;
  to?: Date;
}
interface AlertFilter {
  discrepancy_type?: string;
  untracked?: string;
  arrival?: string;
  pod_not_received?: string;
  pending_invoice?: string;
  cust_id?: string;
}

interface AlertBucket {
  label: string;
  count: number;
  filters: AlertFilter;
}


interface CustomerOption {
  cust_code: string;
  cust_name: string;
}

interface BranchOption {
  booking_branch: string;
  branch_name: string;
}

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  onDateRangeChange?: (dateRange: DateRange) => void;
  dateRange?: DateRange;
  alertCards: AlertCard[];
  activeAlertFilters: AlertFilter;
  onAlertFilterChange: (filters: AlertFilter) => void;
  customerOptions: CustomerOption[];
  selectedCustId?: string;
  onCustomerChange: (code: string | undefined) => void;
  branchOptions: BranchOption[];
  selectedOuCode?: string;
  onBranchChange: (ouCode: string | undefined) => void;
  isLoadingAlerts: boolean;
  alertSkeletonCount?: number;
  shipmentStatus?: string;
  onShipmentStatusChange?: (value: string | undefined) => void;
  geocodingErrorsCount?: number;
  onGeocodingErrorsClick?: () => void;
}
// const TOP_FILTERS = [
//   { label: "Selected", count: 487, id: 'selected' },
//   { label: "Delayed", count: 118, id: 'delayed' },
//   { label: "Starred", count: 0, id: 'starred' },
//   { label: "EWB Expired", count: 0, id: 'ewb_expired' },
//   { label: "Untracked", count: 4, id: 'untracked' },
//   { label: "Round Trips", count: 2, id: 'round_trips' },
// ];


/**
 * Helper to normalize values for comparison, treating undefined, null, and empty string as equivalent
 * when checking for the absence of a filter.
 */
const areValuesEqual = (val1: any, val2: any): boolean => {
  const normalize = (val: any) => (val === undefined || val === null || val === '') ? undefined : val;
  return normalize(val1) === normalize(val2);
};

/**
 * Checks if a card/bucket filter definition matches the currently active alert filter state.
 * Only compares alert-specific properties, ignoring cust_id which is handled by a separate state.
 */
const isFilterActive = (cardFilters: AlertFilter, activeAlertFilters: AlertFilter): boolean => {
  const location = window.location.pathname.toLowerCase();

  if (location.includes("delivered")) {
    const podMatch = areValuesEqual(cardFilters.pod_not_received, activeAlertFilters.pod_not_received);
    const invoiceMatch = areValuesEqual(cardFilters.pending_invoice, activeAlertFilters.pending_invoice);
    return podMatch && invoiceMatch;
  } else {
    const discrepancyMatch = areValuesEqual(cardFilters.discrepancy_type, activeAlertFilters.discrepancy_type);
    const untrackedMatch = areValuesEqual(cardFilters.untracked, activeAlertFilters.untracked);
    const arrivalMatch = areValuesEqual(cardFilters.arrival, activeAlertFilters.arrival);
    return discrepancyMatch && untrackedMatch && arrivalMatch;
  }
};


export function DataTableToolbar<TData>({
  table,
  onDateRangeChange,
  dateRange,
  alertCards,
  activeAlertFilters,
  onAlertFilterChange,
  customerOptions,
  selectedCustId,
  onCustomerChange,
  branchOptions,
  selectedOuCode,
  onBranchChange,
  isLoadingAlerts,
  alertSkeletonCount = 4,
  shipmentStatus,
  onShipmentStatusChange = () => { },
  geocodingErrorsCount = 0,
  onGeocodingErrorsClick = () => { },
}: DataTableToolbarProps<TData>) {
  const location = useLocation();
  const isInTransitPage = location.pathname.toLowerCase().includes("in-transit");
  const noAlertCards = location.pathname.toLowerCase().includes("local") || location.pathname.toLowerCase().includes("dmv");
  const [date, setDate] = useState<DateRange>(dateRange || { from: undefined, to: undefined });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState<Date | null>(null); // Temporary storage for from date

  const [searchQuery, setSearchQuery] = useState(table.getState().globalFilter ?? "");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  React.useEffect(() => {
    if (table.getState().globalFilter !== debouncedSearchQuery) {
      table.setGlobalFilter(debouncedSearchQuery || "");
    }
  }, [debouncedSearchQuery, table]);

  // State to manage the multi-select top filters (e.g., Selected, Delayed)
  // const [selectedTopFilters, setSelectedTopFilters] = useState<string[]>(['selected']); 
  const customerComboboxOptions: ComboboxOption[] = [
    { value: '', label: 'All Customers' },
    ...customerOptions
      .filter(c => c.cust_code && c.cust_code !== '')
      .map(c => ({ value: c.cust_code, label: c.cust_name }))
  ];

  const branchComboboxOptions: ComboboxOption[] = [
    { value: '', label: 'All Branches' },
    ...branchOptions
      .filter(b => b.booking_branch && b.booking_branch !== '')
      .map(b => ({ value: b.booking_branch, label: b.branch_name }))
  ];

  const areAlertsFiltered = Object.keys(activeAlertFilters).length > 0;
  const isFiltered = searchQuery.length > 0 || (date?.from || date?.to) || !!selectedCustId || !!selectedOuCode || areAlertsFiltered || !!shipmentStatus;

  React.useEffect(() => {
    setDate(dateRange || { from: undefined, to: undefined });
  }, [dateRange]);

  const handleDateSelect = (selectedDate: Date | DateRange | undefined) => {
    if (!selectedDate) return;

    // Handle date selection logic
    if (selectedDate instanceof Date) {
      if (!tempFromDate) {
        setTempFromDate(selectedDate);
        setDate({ from: selectedDate, to: undefined });
      } else {
        const newRange = {
          from: tempFromDate,
          to: selectedDate >= tempFromDate ? selectedDate : tempFromDate,
        };
        setDate(newRange);
        setTempFromDate(null);
        if (onDateRangeChange) {
          onDateRangeChange(newRange);
        }
        setIsDatePickerOpen(false);
      }
    }
  };

  // const handleTopFilterClick = (id: string) => {
  //   setSelectedTopFilters(prev => {
  //     if (prev.includes(id)) {
  //       return prev.filter(filterId => filterId !== id);
  //     } else {
  //       return [...prev, id];
  //     }
  //   });
  //   console.log(`Toggled top filter: ${id}`);
  // };

  const handleCustomerChange = (code: string) => {
    const custId = code === '' ? undefined : code;

    onCustomerChange(custId);
  };

  const handleBranchChange = (code: string) => {
    const ouCode = code === '' ? undefined : code;

    onBranchChange(ouCode);
  };

  const clearFilters = () => {
    table.setGlobalFilter("");
    setSearchQuery("");
    setDate({ from: undefined, to: undefined });
    setTempFromDate(null);
    onCustomerChange(undefined);
    onBranchChange(undefined);
    onShipmentStatusChange(undefined);
    if (onDateRangeChange) {
      onDateRangeChange({ from: undefined, to: undefined });
    }
    onAlertFilterChange({});
  };
  const handleCardClick = (card: AlertCard) => {
    if (card.type === 'single' && card.filters) {
      const isCurrentlyActive = isFilterActive(card.filters, activeAlertFilters);
      // Toggle logic: If filter is active, clear it. Otherwise, apply new filter.
      onAlertFilterChange(isCurrentlyActive ? {} : card.filters);
    }
  };

  const handleBucketClick = (bucket: AlertBucket) => {
    const isCurrentlyActive = isFilterActive(bucket.filters, activeAlertFilters);
    // Toggle logic: If filter is active, clear it. Otherwise, apply new filter.
    onAlertFilterChange(isCurrentlyActive ? {} : bucket.filters);
  };

  return (
    <div className="flex flex-col space-y-1">
      {/* <div className="flex space-x-3 overflow-x-auto pb-3">
        {TOP_FILTERS.map(filter => {
          const isActive = selectedTopFilters.includes(filter.id);
          const Icon = isActive ? CheckSquare : Square;

          return (
            <div
              key={filter.id}
              className={cn(
                  "flex-shrink-0 cursor-pointer h-8 px-3 py-1.5 text-sm transition-colors duration-150 border rounded-lg",
                  "flex items-center space-x-2 whitespace-nowrap",
                  isActive
                      ? "bg-amber-100 border-amber-400 text-gray-900 font-medium hover:bg-amber-200" 
                      : "bg-white border-gray-300 text-gray-600 font-normal hover:bg-gray-50"
              )}
              onClick={() => handleTopFilterClick(filter.id)}
            >
              <Icon 
                className={cn("w-4 h-4", isActive ? "text-amber-600 fill-amber-300" : "text-gray-500")} 
              />
              <span className="font-normal">{filter.label}</span>
              <span className={cn(
                  "font-semibold",
                  isActive ? "text-amber-800" : "text-gray-800"
              )}>
                  {filter.count}
              </span>
            </div>
          );
        })}
      </div> */}

      {isLoadingAlerts ? (
        <AlertCardSkeleton count={alertSkeletonCount} />
      ) : (
        <div className={`flex gap-4 ${noAlertCards ? 'pb-0' : 'pb-4'}`}>
          {alertCards.map((card) => {
            const isCardActive = card.filters && isFilterActive(card.filters, activeAlertFilters);
            const hasActiveBucket = card.buckets && card.buckets.some(bucket => isFilterActive(bucket.filters, activeAlertFilters));
            const isActive = Boolean(isCardActive || hasActiveBucket);

            return (
              <AlertCardComponent
                key={card.id}
                card={card}
                isActive={isActive}
                hasActiveBucket={hasActiveBucket || false}
                onCardClick={handleCardClick}
                onBucketClick={handleBucketClick}
                isFilterActive={isFilterActive}
                activeAlertFilters={activeAlertFilters}
              />
            );
          })}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex flex-1 items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search All Trips"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="h-8 w-[120px] lg:w-[180px] pl-8 text-xs font-normal text-gray-600 placeholder:text-gray-500"
            />
          </div>

          <div className="h-8 w-[120px] lg:w-[160px] flex-shrink-0">
            <Combobox
              options={branchComboboxOptions}
              value={selectedOuCode || ''}
              onValueChange={handleBranchChange}
              placeholder="All Branches"
              searchPlaceholder="Search branches..."
              emptyText="No branch found."
              buttonClassName="h-8 text-xs font-normal text-gray-600"
            />
          </div>
          <div className="h-8 w-[120px] lg:w-[160px] flex-shrink-0">
            <Combobox
              options={customerComboboxOptions}
              value={selectedCustId || ''}
              onValueChange={handleCustomerChange}
              placeholder="All Customers"
              searchPlaceholder="Search customers..."
              emptyText="No customer found."
              buttonClassName="h-8 text-xs font-normal text-gray-600"
            />
          </div>
          <div className="flex items-center border rounded-md bg-background">
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <div className="flex divide-x">
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => setIsDatePickerOpen(true)}
                    className={cn(
                      "h-7 px-2 py-1 justify-start text-left font-normal rounded-none rounded-l-md hover:bg-muted/50 text-xs",
                      !date?.from && "text-gray-500"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-normal text-gray-600">From Date</span>
                      {date?.from && <span className="text-xs truncate">{format(date.from, "dd MMM yyyy")}</span>}
                    </div>
                  </Button>
                </PopoverTrigger>

                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => setIsDatePickerOpen(true)}
                    className={cn(
                      "h-7 px-2 py-1 justify-start text-left font-normal rounded-none rounded-r-md hover:bg-muted/50 text-xs",
                      !date?.to && "text-gray-500"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-normal text-gray-600">To Date</span>
                      {date?.to && <span className="text-xs truncate">{format(date.to, "dd MMM yyyy")}</span>}
                    </div>
                  </Button>
                </PopoverTrigger>
              </div>

              <PopoverContent className="w-auto p-0" align="start">
                <div className="p-4">
                  <div className="text-sm font-semibold mb-3 text-gray-900">
                    {!tempFromDate ? "Select start date" : "Select end date"}
                  </div>
                  <Calendar
                    mode="single"
                    selected={tempFromDate || date?.from}
                    onSelect={handleDateSelect}
                    numberOfMonths={2}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (tempFromDate) {
                        return date < tempFromDate;
                      }
                      return false;
                    }}
                  />
                  <div className="flex justify-end mt-4 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsDatePickerOpen(false);
                        setTempFromDate(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Shipment Status Filter */}
          {isInTransitPage && (
            <Select
              value={shipmentStatus ?? "all"}
              onValueChange={(val) => onShipmentStatusChange(val === "all" ? undefined : val)}
            >
              <SelectTrigger
                className={cn(
                  "h-8 w-[148px] text-xs font-normal focus:ring-0 focus:ring-offset-0",
                  shipmentStatus ? "text-primary" : "text-gray-600"
                )}
              >
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Statuses</SelectItem>
                <SelectItem value="on_hold" className="text-xs">On Hold</SelectItem>
                <SelectItem value="incident_reported" className="text-xs">Incident Reported</SelectItem>
                <SelectItem value="beyond_edd" className="text-xs">Beyond EDD</SelectItem>
                <SelectItem value="on_schedule" className="text-xs">On Schedule</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* Geocoding Errors Button */}
          {isInTransitPage && (
            <Button
              variant="outline"
              onClick={onGeocodingErrorsClick}
              className={cn(
                "h-8 px-3 text-xs",
                geocodingErrorsCount > 0 ? "text-red-600 border-red-200 hover:bg-red-50" : "text-gray-600"
              )}
            >
              <MapPin className="mr-2 h-3 w-3" />
              Geocoding Errors
              {geocodingErrorsCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                  {geocodingErrorsCount}
                </span>
              )}
            </Button>
          )}

          {(isFiltered) && (
            <Button variant="ghost" onClick={clearFilters} className="h-8 px-3">
              Reset filter
              <X className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
